import { useMemo, useState, useCallback } from 'react';
import {
	BigNum,
	PublicKey,
	QUOTE_PRECISION_EXP,
	SpotBalanceType,
	UserStatsAccount,
} from '@drift-labs/sdk';
import { COMMON_UI_UTILS, MAIN_POOL_ID, matchEnum } from '@drift/common';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import {
	ACCOUNT_HEALTH_PERCENT_CRITICAL,
	LP_SUBACCOUNT_NAME,
	NEW_LP_SUBACCOUNT_NAME,
	SUPER_STAKE_ACCOUNT_NAMES,
} from 'src/constants/constants';
import { useAggregateAccountsSpotBalances } from 'src/hooks/useAccountBankBalances';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftAccountStore, {
	AccountDataRent,
} from 'src/stores/useDriftAccountsStore';
import NumLib from 'src/utils/NumLib';
import { getTotalIsolatedDepositsForUser } from 'src/stores/DriftStore/helpers/accountDataHelpers';
import useModal from 'src/components/Modals/hooks/useModal';
import { useSubAccountsDelegation } from 'src/hooks/wallets/privy/useSubAccountsDelegation';
import { usePrivyEmbeddedWalletWithFallback } from 'src/hooks/wallets/privy/mappings/usePrivyEmbeddedWalletWithFallback';
import { AccountsTableProps } from 'src/components/PortfolioPage/PortfolioAccountsSubPage/AccountsTable/types';
import { useReclaimRent } from 'src/hooks/driftActions/user/useReclaimRent';
import useShowAccountValues from 'src/hooks/useShowAccountValues';
import { useAllAccountsMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import { useAllAccountsPositions } from 'src/hooks/query/accountTradingData/usePositions';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { canClaimRent } from 'src/components/ReclaimableRentContent';

export interface AccountsTableItem {
	userKey: string;
	authority: PublicKey;
	userId: number;
	accountName: string;
	acctSpotDeposits: BigNum;
	netUnsettled: BigNum;
	acctBorrows: BigNum;
	acctNetValue: BigNum;
	delegate: PublicKey;
	health: number;
	pubKey: PublicKey;
	rent: AccountDataRent;
	openPositionCount: number;
	poolId: number;
	isSuperStakeAccount: boolean;
	isIsolatedAccount: boolean;
	isLpAccount: boolean;
	isDelegated: boolean;
	isDelegatedToEmbeddedWallet: boolean;
	isDelegatedToNonEmbeddedWallet: boolean;
	hasClaimableRent: boolean;
}

export interface UseAccountsTable {
	data: AccountsTableItem[];
	userStatsAccount: UserStatsAccount | undefined;
	claimingRentAccountId: number | undefined;
	hasNonEmbeddedWalletDelegate: boolean;
	isDelegateVariant: boolean;
	numAccountsAtRisk: number;
	sumDeposits: BigNum;
	sumBorrows: BigNum;
	sumNetUsdValue: BigNum;
	showAccountValues: boolean;
	handleReclaimRent: (userId: number) => void;
	openDepositModal: (userId: number, authority: PublicKey) => void;
	openWithdrawModal: (userId: number, authority: PublicKey) => void;
	openTransferModal: (userId: number, authority: PublicKey) => void;
	openUpdateDelegateModal: (
		userId: number,
		authority: PublicKey,
		delegate: PublicKey
	) => void;
	openDeleteAccountModal: (userId: number) => void;
	openEditAccountModal: (userId: number) => void;
}

export default function useAccountsTable({
	accounts,
	variant,
}: Required<AccountsTableProps>): UseAccountsTable {
	const driftClient = useDriftClient();
	const accountBalances = useAggregateAccountsSpotBalances();
	const showAccountValues = useShowAccountValues();
	const connected = useWalletIsConnected();
	const userStatsAccount = useDriftStore((s) => s.userStatsAccount);
	const embeddedWalletPublicKey = usePrivyEmbeddedWalletWithFallback();
	const { allAuthorityAccounts } = useSubAccountsDelegation();

	const setAccounts = useDriftAccountStore((s) => s.set);
	const { show } = useModal();
	const { reclaimRent } = useReclaimRent();

	const { data: allMarginInfos } = useAllAccountsMarginInfo();
	const { data: allPositions } = useAllAccountsPositions();

	const [claimingRentAccountId, setClaimingRentAccountId] = useState<number>();

	const hasNonEmbeddedWalletDelegate = useMemo(() => {
		return (
			allAuthorityAccounts.length > 0 &&
			allAuthorityAccounts.some(
				(account) =>
					(!embeddedWalletPublicKey ||
						!account.delegate.equals(embeddedWalletPublicKey)) &&
					!account.delegate.equals(DEFAULT_PUBLIC_KEY)
			)
		);
	}, [allAuthorityAccounts, embeddedWalletPublicKey]);

	const data: AccountsTableItem[] = useMemo(() => {
		return accounts.map((acct) => {
			const subAccountBalances = accountBalances.filter(
				(bal) => bal.accountKey == acct.userKey
			);

			const isSuperStakeAccount = SUPER_STAKE_ACCOUNT_NAMES.includes(acct.name);
			const isIsolatedAccount = acct.poolId !== MAIN_POOL_ID;
			const isLpAccount = [LP_SUBACCOUNT_NAME, NEW_LP_SUBACCOUNT_NAME].includes(
				acct.name
			);

			const isDelegated = !acct.delegate.equals(DEFAULT_PUBLIC_KEY);
			const isDelegatedToEmbeddedWallet =
				!!embeddedWalletPublicKey &&
				acct.delegate.equals(embeddedWalletPublicKey);
			const isDelegatedToNonEmbeddedWallet =
				isDelegated && !isDelegatedToEmbeddedWallet;

			const hasClaimableRent = !!(
				acct.rent.loaded &&
				userStatsAccount?.authority &&
				canClaimRent(userStatsAccount, acct.rent.amount)
			);

			const marginInfo = allMarginInfos?.[acct.userKey];
			const positions = allPositions?.[acct.userKey];

			return {
				userKey: acct.userKey,
				authority: acct.authority,
				userId: acct.userId,
				accountName: acct.name,
				acctSpotDeposits: NumLib.sumBigNums(
					subAccountBalances
						.filter((bal) =>
							matchEnum(bal.balanceType, SpotBalanceType.DEPOSIT)
						)
						.map((bal) => bal.quoteValue),
					QUOTE_PRECISION_EXP
				).add(
					acct.client?.isSubscribed
						? BigNum.from(
								getTotalIsolatedDepositsForUser(acct.client),
								QUOTE_PRECISION_EXP
							)
						: BigNum.zero()
				),
				netUnsettled: acct.client?.isSubscribed
					? BigNum.from(
							acct.client.getUnrealizedPNL(true) ?? 0,
							QUOTE_PRECISION_EXP
						)
					: BigNum.zero(),
				acctBorrows: NumLib.sumBigNums(
					subAccountBalances
						.filter((bal) => matchEnum(bal.balanceType, SpotBalanceType.BORROW))
						.map((bal) => bal.quoteValue),
					QUOTE_PRECISION_EXP
				),
				acctNetValue: BigNum.from(marginInfo?.netUsdValue, QUOTE_PRECISION_EXP),
				delegate: acct?.delegate,
				health:
					connected && acct.client?.isSubscribed
						? (acct.client.getHealth() ?? 0)
						: 100,
				pubKey: acct.pubKey,
				rent: acct.rent,
				openPositionCount: positions?.openPerpPositions?.length ?? 0,
				poolId:
					connected && acct.client?.isSubscribed
						? (acct.client.getUserAccount()?.poolId ?? MAIN_POOL_ID)
						: MAIN_POOL_ID,
				isSuperStakeAccount,
				isIsolatedAccount,
				isLpAccount,
				isDelegated,
				isDelegatedToEmbeddedWallet,
				isDelegatedToNonEmbeddedWallet,
				hasClaimableRent,
			};
		});
	}, [
		accounts,
		connected,
		embeddedWalletPublicKey,
		userStatsAccount,
		accountBalances,
		allMarginInfos,
		allPositions,
	]);

	const isDelegateVariant = variant === 'delegate';

	const numAccountsAtRisk =
		data?.filter((row) => row.health < ACCOUNT_HEALTH_PERCENT_CRITICAL)
			.length ?? 0;

	const sumDeposits = data
		? NumLib.sumBigNums(
				data?.map((row) => row?.acctSpotDeposits),
				QUOTE_PRECISION_EXP
			)
		: BigNum.zero();

	const sumBorrows = data
		? NumLib.sumBigNums(
				data.map((row) => row?.acctBorrows),
				QUOTE_PRECISION_EXP
			)
		: BigNum.zero();

	const sumNetUsdValue = data
		? NumLib.sumBigNums(
				data.map((row) => row?.acctNetValue),
				QUOTE_PRECISION_EXP
			)
		: BigNum.zero();

	const openEditAccountModal = useCallback(
		(idToEdit: number) => {
			show({ type: 'edit_subaccount', props: { subaccountId: idToEdit } });
		},
		[show]
	);

	const openDeleteAccountModal = useCallback(
		(idToDelete: number) => {
			show({ type: 'delete_subaccount', props: { subaccountId: idToDelete } });
		},
		[show]
	);

	const openDepositModal = useCallback(
		(userId: number, authority: PublicKey) => {
			const userKey = COMMON_UI_UTILS.getUserKey(userId, authority);

			show({ type: 'deposit', props: { targetAccountKey: userKey } });
		},
		[show]
	);

	const openWithdrawModal = useCallback(
		(userId: number, authority: PublicKey) => {
			const userKey = COMMON_UI_UTILS.getUserKey(userId, authority);

			show({ type: 'withdraw', props: { targetAccountKey: userKey } });
		},
		[show]
	);

	const openTransferModal = useCallback(
		(userId: number, authority: PublicKey) => {
			const userKey = COMMON_UI_UTILS.getUserKey(userId, authority);

			show({ type: 'transfer', props: { targetAccountKey: userKey } });
		},
		[show]
	);

	const handleReclaimRent = useCallback(
		async (userId: number) => {
			setClaimingRentAccountId(userId);

			await reclaimRent(userId);

			setClaimingRentAccountId(undefined);
		},
		[reclaimRent]
	);

	const openUpdateDelegateModal = useCallback(
		(userId: number, authority: PublicKey, delegate: PublicKey) => {
			// Switch to subaccount. Migrated from previous logic, not sure why it's needed.
			const userKey = COMMON_UI_UTILS.getUserKey(userId, authority);
			setAccounts((s) => {
				s.currentUserKey = userKey;
			});
			driftClient?.switchActiveUser(userId, authority);

			// Open modal.
			show({
				type: 'delegate_account',
				props: { delegateAuthority: delegate },
			});
		},
		[show, setAccounts, driftClient]
	);

	return {
		data,
		userStatsAccount,
		claimingRentAccountId,
		hasNonEmbeddedWalletDelegate,
		isDelegateVariant,
		numAccountsAtRisk,
		sumDeposits,
		sumBorrows,
		sumNetUsdValue,
		showAccountValues,
		openEditAccountModal,
		openDeleteAccountModal,
		openDepositModal,
		openWithdrawModal,
		openTransferModal,
		handleReclaimRent,
		openUpdateDelegateModal,
	};
}
